import { z } from "zod";

/**
 * A project is the tenant. The person who creates it is its owner. Members hold one of the other
 * roles. Only the owner pays and manages members.
 */
export const projectRoles = ["owner", "developer", "approver", "viewer"] as const;
export const projectRoleSchema = z.enum(projectRoles);
export type ProjectRole = z.infer<typeof projectRoleSchema>;

export const projectStatuses = ["active", "archived"] as const;
export const projectStatusSchema = z.enum(projectStatuses);
export type ProjectStatus = z.infer<typeof projectStatusSchema>;

/** Lower-case letters, digits, and single hyphens. Used in links and audit entries. */
export const projectSlugSchema = z
  .string()
  .min(3)
  .max(48)
  .regex(/^[a-z0-9]+(?:-[a-z0-9]+)*$/, "Use lower-case letters, digits, and single hyphens.");
export type ProjectSlug = z.infer<typeof projectSlugSchema>;

/**
 * `POST /api/v1/projects`. Any signed-in user can create a project and becomes its owner.
 * The slug cannot change later, so the create dialog previews it before anything is stored.
 */
export const createProjectSchema = z.object({
  name: z.string().trim().min(1).max(120),
  slug: projectSlugSchema,
  description: z.string().trim().max(500).optional(),
});
export type CreateProject = z.infer<typeof createProjectSchema>;

/**
 * One project in `GET /api/v1/projects`: the caller's projects only, with their role, live
 * counts, and balance. The balance is null when no wallet exists yet, so the picker never shows
 * zero for an unknown amount. The grant expiry is null when no grant was recorded, and drives
 * the picker's expiring-grant attention state.
 */
export const projectSummarySchema = z.object({
  id: z.uuid(),
  name: z.string().min(1),
  slug: projectSlugSchema,
  description: z.string().nullable(),
  role: projectRoleSchema,
  status: projectStatusSchema,
  runningRuns: z.number().int().min(0),
  pendingApprovals: z.number().int().min(0),
  creditBalance: z.number().int().nullable(),
  welcomeGrantExpiresAt: z.iso.datetime().nullable(),
});
export type ProjectSummary = z.infer<typeof projectSummarySchema>;

export const projectSummaryListSchema = z.array(projectSummarySchema);
export type ProjectSummaryList = z.infer<typeof projectSummaryListSchema>;
